import React, {useEffect, useMemo, useState} from "react";
import {Alert, App, Button, Modal, Typography, Upload} from "antd";
import Shell from "../../exchange/v1/ui/Shell";
import api from "../../../api/axiosClient";
import {useSelector} from "react-redux";
import {selectExchangeById} from "../../../store/exchangeActiveSlice";

const {Text} = Typography;

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function normalizeStatus(s) {
  return String(s || "").toUpperCase();
}

function statusLabel(s) {
  const v = normalizeStatus(s);
  if (v === "RECEIVED") return "Получено";
  if (v === "SENT") return "Отправлено";
  if (v === "CREATED") return "Создано";
  if (v === "DISPUTE") return "Спор";
  if (v === "CANCELLED") return "Отменено";
  return v || "—";
}

function toStr(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function clip(v, n = 20) {
  const s = toStr(v);
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function adaptTransferLocal(t) {
  if (!t) return null;

  const raw = t.raw ? t.raw : t;

  return {
    id: t?.id ?? null,
    exchange: t?.exchange ?? null,

    payer_type: t?.payer_type ?? null,
    payer_user: t?.payer_user ?? null,
    payee_type: t?.payee_type ?? null,
    payee_user: t?.payee_user ?? null,

    currency: t?.currency ?? null,
    amount: t?.amount ?? null,

    related_transaction: t?.related_transaction ?? null,

    status: normalizeStatus(t?.status),
    payer_sent_confirmed: !!t?.payer_sent_confirmed,
    payer_sent_confirmed_at: t?.payer_sent_confirmed_at ?? null,
    payee_received_confirmed: !!t?.payee_received_confirmed,
    payee_received_confirmed_at: t?.payee_received_confirmed_at ?? null,

    created_at: t?.created_at ?? null,

    raw,
  };
}

function payoutLabel(p) {
  const type = p?.type || "—";
  const detail = p?.detail || "—";
  const methodTitle = p?.pay_method?.method?.title || p?.pay_method?.method?.code || "—";
  const cur = p?.pay_method?.currency || null;

  const left = `${type}: ${detail}`;
  const right = `${methodTitle}${cur ? ` (${cur})` : ""}`;
  return `${left} · ${right}`;
}

function getPayeePayoutsList(t) {
  const raw = t?.raw || {};
  const payouts = raw?.payee_payouts || t?.payee_payouts || [];
  const list = Array.isArray(payouts) ? payouts : [];
  return list.filter((x) => !!x?.id);
}

function getPayeeRequisitesViewFromPayout(p) {
  if (!p) {
    return {
      detail: null,
      methodTitle: null,
      currency: null,
      type: null,
      source: "none",
      payoutId: null,
    };
  }

  return {
    detail: p?.detail ?? null,
    methodTitle: p?.pay_method?.method?.title || p?.pay_method?.method?.code || null,
    currency: p?.pay_method?.currency || null,
    type: p?.type || null,
    source: "payee_payouts",
    payoutId: p?.id ?? null,
  };
}

function getPayeeRequisitesViewFallback(t) {
  const raw = t?.raw || {};

  const detail =
    raw?.payee_requisites ||
    raw?.payee_payout_detail ||
    raw?.payout_detail ||
    raw?.requisites ||
    raw?.detail ||
    null;

  const methodTitle = raw?.payee_method_title || raw?.method_title || raw?.method || null;

  return {
    detail,
    methodTitle,
    currency: null,
    type: null,
    source: "fallback",
    payoutId: null,
  };
}

export default function ExchangeStageTransfers({
  stage,
  onContinue,
  onBack,
  tx,
  currentUserId,
  showReceiveSection = true,
}) {
  const {message} = App.useApp();

  const currentUserIdFromStore = useSelector((s) => s?.auth?.user?.id || null);
  const uidEffective = String(currentUserId || currentUserIdFromStore || "");

  const txId = tx?.id || tx?.raw?.id || null;
  const txFromStore = useSelector(selectExchangeById(txId));
  const txEffective = txFromStore || tx || null;

  const [now, setNow] = useState(Date.now());

  const createdAt = txEffective?.created_at || txEffective?.raw?.created_at || null;

  const {deadline, valid} = useMemo(() => {
    if (!createdAt) return {deadline: null, valid: false};
    const ts = new Date(createdAt).getTime();
    if (Number.isNaN(ts)) return {deadline: null, valid: false};
    return {deadline: ts + TWO_HOURS_MS, valid: true};
  }, [createdAt]);

  const msLeft = valid ? deadline - now : null;
  const expired = valid && msLeft <= 0;

  useEffect(() => {
    if (!valid) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [valid]);

  const transfersForView = useMemo(() => {
    const raw1 = txFromStore?.transfers;
    const raw2 = txFromStore?.raw?.transfers;
    const raw3 = txEffective?.transfers;
    const raw4 = txEffective?.raw?.transfers;

    const src =
      (Array.isArray(raw1) && raw1.length ? raw1 : null) ||
      (Array.isArray(raw3) && raw3.length ? raw3 : null) ||
      (Array.isArray(raw2) && raw2.length ? raw2 : null) ||
      (Array.isArray(raw4) && raw4.length ? raw4 : null) ||
      [];

    return (src || []).map(adaptTransferLocal).filter((x) => !!x?.id);
  }, [txFromStore, txEffective]);

  const [transfers, setTransfers] = useState(transfersForView);

  useEffect(() => {
    setTransfers(transfersForView);
  }, [transfersForView]);

  const [proofOpen, setProofOpen] = useState(false);
  const [proofTransferId, setProofTransferId] = useState(null);
  const [proofFileList, setProofFileList] = useState([]);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // выбор payout для конкретного transfer
  const [payoutPickOpen, setPayoutPickOpen] = useState(false);
  const [payoutPickTransferId, setPayoutPickTransferId] = useState(null);
  const [payoutPickSelectedId, setPayoutPickSelectedId] = useState(null);
  const [selectedPayoutByTransferId, setSelectedPayoutByTransferId] = useState({}); // { [transferId]: payoutId }

  // обязательное модальное подтверждение получения
  const [receiveConfirmOpen, setReceiveConfirmOpen] = useState(false);
  const [receiveConfirmTransferId, setReceiveConfirmTransferId] = useState(null);

  const openProofModal = (transferId) => {
    setProofTransferId(transferId || null);
    setProofFileList([]);
    setProofOpen(true);
  };

  const closeProofModal = () => {
    setProofOpen(false);
    setProofTransferId(null);
    setProofFileList([]);
    setConfirmLoading(false);
  };

  const openPayoutPickModal = (transferId) => {
    const tid = transferId || null;
    if (!tid) return;

    const t = (Array.isArray(transfers) ? transfers : []).find((x) => String(x?.id) === String(tid));
    const list = getPayeePayoutsList(t);

    if (list.length <= 1) return;

    const currentSelected = selectedPayoutByTransferId[String(tid)] || null;
    const fallbackFirst = list[0]?.id || null;

    setPayoutPickTransferId(tid);
    setPayoutPickSelectedId(currentSelected || fallbackFirst);
    setPayoutPickOpen(true);
  };

  const closePayoutPickModal = () => {
    setPayoutPickOpen(false);
    setPayoutPickTransferId(null);
    setPayoutPickSelectedId(null);
  };

  const confirmPayoutPick = () => {
    const tid = payoutPickTransferId;
    const pid = payoutPickSelectedId;
    if (!tid || !pid) {
      closePayoutPickModal();
      return;
    }

    setSelectedPayoutByTransferId((prev) => ({
      ...(prev || {}),
      [String(tid)]: String(pid),
    }));

    closePayoutPickModal();
  };

  const openReceiveConfirmModal = (transferId) => {
    if (!transferId) return;
    setReceiveConfirmTransferId(transferId);
    setReceiveConfirmOpen(true);
  };

  const closeReceiveConfirmModal = () => {
    if (confirmLoading) return; // не даём закрыть, пока идёт запрос
    setReceiveConfirmOpen(false);
    setReceiveConfirmTransferId(null);
  };

  const updateTransferInState = (updatedRaw) => {
    const updated = adaptTransferLocal(updatedRaw);
    const id = updated?.id;
    if (!id) return;

    setTransfers((prev) => {
      const xs = Array.isArray(prev) ? prev : [];
      return xs.map((t) => (String(t?.id) === String(id) ? {...t, ...updated} : t));
    });
  };

  const confirmSent = async () => {
    const transferId = proofTransferId;
    if (!transferId) return;

    const fileObj = proofFileList?.[0]?.originFileObj || null;
    if (!fileObj) {
      message.error("При подтверждении отправки требуется скрин/документ");
      return;
    }

    try {
      setConfirmLoading(true);
      const form = new FormData();
      form.append("payer_sent_confirmed", "true");
      form.append("file", fileObj);

      const res = await api.post(`/api/exh/transfers/${transferId}/confirm-sent/`, form, {
        headers: {"Content-Type": "multipart/form-data"},
      });

      if (res?.data) updateTransferInState(res.data);

      message.success("Отправка подтверждена");
      closeProofModal();
    } catch (e) {
      const data = e?.response?.data;
      if (data) {
        const firstField = Object.keys(data)[0];
        const firstMsg = Array.isArray(data[firstField]) ? data[firstField][0] : String(data[firstField]);
        message.error(firstMsg || "Не удалось подтвердить отправку");
        return;
      }
      message.error("Не удалось подтвердить отправку");
    } finally {
      setConfirmLoading(false);
    }
  };

  const confirmReceived = async (transferId) => {
    if (!transferId) return;

    try {
      setConfirmLoading(true);
      const res = await api.post(`/api/exh/transfers/${transferId}/confirm-received/`, {
        payee_received_confirmed: true,
      });

      if (res?.data) updateTransferInState(res.data);

      message.success("Получение подтверждено");
      closeReceiveConfirmModal();
    } catch (e) {
      const data = e?.response?.data;
      if (data) {
        const firstField = Object.keys(data)[0];
        const firstMsg = Array.isArray(data[firstField]) ? data[firstField][0] : String(data[firstField]);
        message.error(firstMsg || "Не удалось подтвердить получение");
        return;
      }
      message.error("Не удалось подтвердить получение");
    } finally {
      setConfirmLoading(false);
    }
  };

  const {senderTransfers, receiverTransfers} = useMemo(() => {
    const xs = Array.isArray(transfers) ? transfers : [];
    const uid = uidEffective ? String(uidEffective) : "";

    const a = uid ? xs.filter((t) => String(t?.payer_user || "") === uid) : [];
    const b = uid ? xs.filter((t) => String(t?.payee_user || "") === uid) : [];

    return {senderTransfers: a, receiverTransfers: b};
  }, [transfers, uidEffective]);

  const allDone = useMemo(() => {
    const sOk = senderTransfers.every((t) => !!t?.payer_sent_confirmed);
    const rOk = receiverTransfers.every((t) => !!t?.payee_received_confirmed);
    return sOk && rOk;
  }, [senderTransfers, receiverTransfers]);

  const canGoNext = useMemo(() => {
    if (expired) return false;
    return allDone;
  }, [expired, allDone]);

  const receiveTransferForModal = useMemo(() => {
    const tid = receiveConfirmTransferId;
    if (!tid) return null;
    return (Array.isArray(transfers) ? transfers : []).find((t) => String(t?.id) === String(tid)) || null;
  }, [receiveConfirmTransferId, transfers]);

  return (
    <Shell stage={stage} title="Переводы" subtitle="Этап 3. Завершите переводы в установленное время."
           minBodyHeight={560}>
      {valid && (
        <div
          className={`mb-4 rounded-[16px] px-6 py-4 text-center font-extrabold ${
            expired ? "bg-red-100 text-red-700" : "bg-[#FFF7E6] text-[#AD6800]"
          }`}
        >
          <div className="text-[14px] uppercase tracking-wide opacity-80">Осталось времени</div>
          <div className="text-[32px] mt-1">{expired ? "00:00:00" : formatCountdown(msLeft)}</div>
        </div>
      )}

      {expired && (
        <Alert
          type="error"
          showIcon
          message="Время истекло"
          description="2 часа на завершение переводов истекли."
        />
      )}

      <div className="mt-6 bg-[#F3F6FF] rounded-[16px] p-6 max-[520px]:p-4">
        <div className="text-[18px] font-extrabold text-[#0B2EA6]">
          Платёжные инструкции{" "}
          <span className="text-[12px] font-extrabold text-black/45">(к отправке: {senderTransfers.length})</span>
        </div>

        <Text className="block mt-1 text-[14px] text-[#6B7280]">
          Отображаются трансферы, где вы плательщик. Для подтверждения отправки обязателен скрин/документ.
        </Text>

        <div className="mt-4 space-y-3">
          {senderTransfers.length ? (
            senderTransfers.map((t) => {
              const id = t?.id;
              const st = normalizeStatus(t?.status);
              const sentConfirmed = !!t?.payer_sent_confirmed;

              const payouts = getPayeePayoutsList(t);
              const pickedId = selectedPayoutByTransferId[String(id)] || null;

              let payeeView = null;
              if (payouts.length) {
                const chosen = payouts.find((p) => String(p?.id) === String(pickedId)) || payouts[0] || null;
                payeeView = getPayeeRequisitesViewFromPayout(chosen);
              } else {
                payeeView = getPayeeRequisitesViewFallback(t);
              }

              const requisites = payeeView?.detail || null;
              const methodTitle = payeeView?.methodTitle || null;
              const methodCur = payeeView?.currency || null;
              const payoutType = payeeView?.type || null;

              return (
                <div key={id} className="rounded-[14px] bg-white border border-black/5 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13px] text-black/55 truncate">{id || "—"}</div>

                      <div className="mt-1 text-[16px] font-extrabold text-[#0B1B3A]">
                        {t?.amount ?? "—"} {t?.currency ?? "—"}
                      </div>

                      <div className="mt-1 text-[12px] text-black/55">
                        Статус: <b>{statusLabel(st)}</b>
                      </div>

                      <div className="mt-2 rounded-[12px] border border-black/5 bg-[#F7FAFF] px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[12px] text-black/45">Куда отправлять</div>

                          {payouts.length > 1 && (
                            <Button
                              size="small"
                              onClick={() => openPayoutPickModal(id)}
                              disabled={expired || sentConfirmed || !id}
                            >
                              Выбрать реквизиты
                            </Button>
                          )}
                        </div>

                        <div className="text-[14px] font-extrabold text-[#0B1B3A] break-words">
                          {requisites ? toStr(requisites) : "—"}
                        </div>

                        <div className="mt-1 text-[12px] text-black/50">
                          Метод:{" "}
                          <b>
                            {methodTitle ? clip(methodTitle, 40) : "—"}
                            {methodCur ? ` (${methodCur})` : ""}
                          </b>
                          {payoutType ? <span className="text-black/45"> · {payoutType}</span> : null}
                        </div>

                        {payouts.length > 1 && (
                          <div className="mt-1 text-[12px] text-black/45">
                            Доступно реквизитов: {payouts.length}. Текущий:{" "}
                            <span className="text-black/60">
                              {clip(
                                payoutLabel(payouts.find((p) => String(p?.id) === String(pickedId)) || payouts[0]),
                                80
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0 flex gap-2">
                      <Button
                        size="small"
                        onClick={() => openProofModal(id)}
                        disabled={expired || sentConfirmed || !id}
                        loading={confirmLoading && proofTransferId === id}
                      >
                        Подтвердить отправку
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-[14px] text-[#6B7280]">Нет трансферов к отправке</div>
          )}
        </div>
      </div>

      {showReceiveSection && (
        <div className="mt-6 bg-[#F3F6FF] rounded-[16px] p-6 max-[520px]:p-4">
          <div className="text-[18px] font-extrabold text-[#0B2EA6]">
            Подтверждение получения{" "}
            <span className="text-[12px] font-extrabold text-black/45">(к получению: {receiverTransfers.length})</span>
          </div>

          <Text className="block mt-1 text-[14px] text-[#6B7280]">
            Отображаются трансферы, где вы получатель. Подтверждение получения не требует файла.
          </Text>

          <div className="mt-4 space-y-3">
            {receiverTransfers.length ? (
              receiverTransfers.map((t) => {
                const id = t?.id;
                const st = normalizeStatus(t?.status);
                const recvConfirmed = !!t?.payee_received_confirmed;

                return (
                  <div key={id} className="rounded-[14px] bg-white border border-black/5 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[13px] text-black/55 truncate">{id || "—"}</div>
                        <div className="mt-1 text-[16px] font-extrabold text-[#0B1B3A]">
                          {t?.amount ?? "—"} {t?.currency ?? "—"}
                        </div>
                        <div className="mt-1 text-[12px] text-black/55">
                          Статус: <b>{statusLabel(st)}</b>
                        </div>
                      </div>

                      <div className="shrink-0 flex gap-2">
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => openReceiveConfirmModal(id)}
                          disabled={expired || recvConfirmed || !id}
                        >
                          Подтвердить получение
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-[14px] text-[#6B7280]">Нет трансферов к получению</div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between gap-3">
        <Button onClick={onBack} disabled>
          Назад
        </Button>
        <Button type="primary" disabled={!canGoNext} onClick={onContinue}>
          Перейти к подтверждениям
        </Button>
      </div>

      <Modal
        title="Выбор реквизитов получателя"
        open={payoutPickOpen}
        onCancel={closePayoutPickModal}
        onOk={confirmPayoutPick}
        okText="Выбрать"
        cancelText="Отмена"
        destroyOnHidden
      >
        {(() => {
          const t = (Array.isArray(transfers) ? transfers : []).find(
            (x) => String(x?.id) === String(payoutPickTransferId)
          );
          const list = getPayeePayoutsList(t);

          if (!list.length) {
            return <div className="text-[14px] text-black/60">Нет доступных реквизитов.</div>;
          }

          return (
            <div className="space-y-2">
              <div className="text-[14px] text-black/60">Выберите реквизиты, на которые нужно отправить средства.</div>

              <div className="mt-3 space-y-2">
                {list.map((p) => {
                  const pid = p?.id;
                  const active = String(pid) === String(payoutPickSelectedId);

                  return (
                    <div
                      key={pid}
                      className={`rounded-[12px] border px-3 py-2 cursor-pointer ${
                        active ? "border-[#0B2EA6] bg-[#EEF2FF]" : "border-black/10 bg-white"
                      }`}
                      onClick={() => setPayoutPickSelectedId(pid)}
                    >
                      <div className="text-[13px] text-black/80">{payoutLabel(p)}</div>
                      <div className="text-[12px] text-black/45 mt-1">ID: {clip(pid, 36)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        title="Подтверждение получения"
        open={receiveConfirmOpen}
        onCancel={closeReceiveConfirmModal}
        onOk={() => confirmReceived(receiveConfirmTransferId)}
        okText="Подтверждаю получение"
        cancelText="Отмена"
        confirmLoading={confirmLoading}
        destroyOnHidden
        maskClosable={false}
        closable={!confirmLoading}
      >
        {(() => {
          const t = receiveTransferForModal;
          if (!t) return <div className="text-[14px] text-black/60">Трансфер не найден.</div>;

          return (
            <div className="space-y-3">
              <div className="text-[14px] text-black/70">
                Подтвердите, что вы действительно получили средства по этому переводу.
              </div>

              <div className="rounded-[12px] border border-black/10 bg-[#F7FAFF] px-3 py-2">
                <div className="text-[12px] text-black/45">Сумма</div>
                <div className="text-[16px] font-extrabold text-[#0B1B3A]">
                  {t?.amount ?? "—"} {t?.currency ?? "—"}
                </div>

                <div className="mt-2 text-[12px] text-black/45">ID трансфера</div>
                <div className="text-[13px] text-black/70 break-words">{t?.id || "—"}</div>
              </div>

              <div className="text-[12px] text-black/45">
                После подтверждения статус обновится. Если вы ещё не получили средства, нажмите «Отмена».
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        title="Подтверждение отправки"
        open={proofOpen}
        onCancel={closeProofModal}
        onOk={confirmSent}
        okText="Подтвердить"
        cancelText="Отмена"
        confirmLoading={confirmLoading}
        destroyOnHidden
      >
        <div className="text-[14px] text-black/60 mb-3">Прикрепите скрин/документ подтверждения оплаты.</div>

        <Upload
          accept="image/*,.pdf"
          maxCount={1}
          fileList={proofFileList}
          beforeUpload={() => false}
          onChange={({fileList}) => setProofFileList(fileList)}
        >
          <Button>Выбрать файл</Button>
        </Upload>

        <div className="mt-3 text-[12px] text-black/45">Разрешены изображения и PDF. Обязательное поле.</div>
      </Modal>
    </Shell>
  );
}